import { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { BusinessModel } from '@/components/pitch/BusinessModel';
import { GoToMarket } from '@/components/pitch/GoToMarket';
import { MarketSignal } from '@/components/pitch/MarketSignal';
import { MarketSummary } from '@/components/pitch/MarketSummary';
import { PitchFooter } from '@/components/pitch/PitchFooter';
import { PitchHero } from '@/components/pitch/PitchHero';
import { PitchProgress } from '@/components/pitch/PitchProgress';
import { Roadmap } from '@/components/pitch/Roadmap';
import { RoiSummary } from '@/components/pitch/RoiSummary';
import { SecurityCompliance } from '@/components/pitch/SecurityCompliance';
import { PITCH_SECTIONS } from '@/components/pitch/sections';
import { TheProblem } from '@/components/pitch/TheProblem';
import { UseOfFunds } from '@/components/pitch/UseOfFunds';
import { GovernedCaseRail } from '@/components/pitch-v5/GovernedCaseRail';
import { PitchV5Product } from '@/components/pitch-v5/PitchV5Product';
import { useGovernedCaseSequence } from '@/components/pitch-v5/useGovernedCaseSequence';
import '@/pitch-v5.css';

interface NavigatorWithConnection extends Navigator {
  connection?: {
    saveData?: boolean;
  };
}

const REVIEW_DWELL_MS = 850;

/**
 * Pitch V5 — the governed case rail.
 *
 * V1 remains the copy and information-architecture authority outside section
 * 01, where this experimental route retains the earlier market evidence view.
 * V5 adds one text-free authored motion system that completes after a short
 * review dwell.
 */
export function PitchV5Page() {
  const reducedMotion = useReducedMotion() ?? false;
  const saveData = useSaveData();
  const staticMotion = reducedMotion || saveData;
  const sequence = useGovernedCaseSequence({
    reducedMotion,
    saveData,
    autoReleaseMs: REVIEW_DWELL_MS,
  });

  useEffect(() => {
    const previous = document.title;
    document.title = 'Asset Hound · Investor run through';
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <div
      className="pitch-v5 relative min-h-screen overflow-x-hidden text-ink"
      data-case-phase={sequence.phase}
      data-v5-static-motion={staticMotion ? 'true' : 'false'}
    >
      <PitchV5Atmosphere />
      <PitchProgress sections={PITCH_SECTIONS} />

      <main className="relative z-10">
        <div className="pitch-v5-hero-stage">
          <PitchHero />
          <GovernedCaseRail
            phase={sequence.phase}
            className="pitch-v5-hero-rail"
          />
        </div>
        <MarketSignal interactiveGraphic />
        <TheProblem />
        <PitchV5Product phase={sequence.phase} />
        <RoiSummary linkToCalculator />
        <MarketSummary />
        <Roadmap />
        <BusinessModel />
        <SecurityCompliance />
        <GoToMarket />
        <UseOfFunds />
      </main>

      <PitchFooter />
    </div>
  );
}

function PitchV5Atmosphere() {
  return (
    <div
      aria-hidden
      className="pitch-v5-atmosphere pointer-events-none fixed inset-0"
    >
      <div className="pitch-v5-grid" />
      <div className="pitch-v5-rule pitch-v5-rule-a" />
      <div className="pitch-v5-rule pitch-v5-rule-b" />
      <div className="pitch-grain pitch-v5-grain absolute inset-0 mix-blend-overlay" />
    </div>
  );
}

function useSaveData() {
  const [saveData] = useState(() => {
    if (typeof navigator === 'undefined') return false;
    return Boolean((navigator as NavigatorWithConnection).connection?.saveData);
  });

  return saveData;
}
